import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/cn';
import { formatCurrency, formatDateTime } from '../lib/format';
import { EmptyState, ErrorNote, Panel, Spinner } from '../components/ui';
import { RangePicker } from '../components/RangePicker';
import { preset, rangeKey, rangeQuery, type RangeValue } from '../lib/range';
import {
  DocumentViewer,
  ReceiptButton,
  type DocumentRequest,
} from '../components/DocumentViewer';

interface Movement {
  transactionId: number;
  at: string;
  type: string;
  amount: string;
  method: string;
  tillName: string;
  description: string | null;
}

interface MovementPage {
  items: Movement[];
  nextCursor: string | null;
}

const DIRECTIONS = [
  { key: '', label: 'All' },
  { key: 'in', label: 'Pay-in' },
  { key: 'out', label: 'Pay-out' },
] as const;

export function CashMovements(): React.JSX.Element {
  const { activeStore } = useAuth();
  const storeId = activeStore?.storeId;
  const [range, setRange] = useState<RangeValue>(preset('last30'));
  const [direction, setDirection] = useState('');
  const [viewing, setViewing] = useState<DocumentRequest | null>(null);

  const { data, error, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['cash', storeId, rangeKey(range), direction],
      queryFn: ({ pageParam }) =>
        api.get<MovementPage>(
          `/stores/${storeId}/cash-movements?${rangeQuery(range)}&limit=50` +
            `${direction ? `&direction=${direction}` : ''}` +
            `${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
        ),
      initialPageParam: '',
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      enabled: Boolean(storeId),
    });

  const movements = data?.pages.flatMap((page) => page.items) ?? [];
  const timeZone = activeStore?.timezone ?? 'Europe/London';

  return (
    <>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="qs-eyebrow">Transactions</p>
          <h1 className="text-lg font-medium tracking-tight">Cash movements</h1>
        </div>
        <RangePicker value={range} onChange={setRange} />
      </header>

      <div className="mb-3 flex gap-1" role="group" aria-label="Filter by direction">
        {DIRECTIONS.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => setDirection(option.key)}
            aria-pressed={direction === option.key}
            className={cn(
              'rounded border px-2 py-1 text-xs',
              direction === option.key
                ? 'border-accent bg-accent-soft font-medium text-accent'
                : 'border-rule text-ink-muted hover:border-rule-strong hover:text-ink',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {error && <ErrorNote error={error} />}

      <Panel>
        {isPending ? (
          <Spinner label="Loading movements…" />
        ) : movements.length === 0 ? (
          <EmptyState>No cash movements in this period.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <caption className="sr-only">Pay-ins and pay-outs</caption>
              <thead>
                <tr className="border-b border-rule">
                  <th className="qs-eyebrow py-2 pl-3 text-left">When</th>
                  {/* The type moves under the timestamp on a phone — see the row
                      below. Four columns of which two cannot wrap (a mono
                      timestamp and a signed amount) do not fit 360px, and the
                      only one that can wrap is a two-word type name, which was
                      then broken mid-word into "Cashba / ck". */}
                  <th className="qs-eyebrow hidden py-2 text-left sm:table-cell">Type</th>
                  <th className="qs-eyebrow hidden py-2 text-left lg:table-cell">Method</th>
                  <th className="qs-eyebrow hidden py-2 text-left lg:table-cell">Till</th>
                  <th className="qs-eyebrow hidden py-2 text-left md:table-cell">Note</th>
                  <th className="qs-eyebrow py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 pr-3">
                    <span className="sr-only">Receipt</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => {
                  // The till's sign convention: positive is money into the
                  // drawer. Left as-is, because rewriting it would not match the
                  // receipt the shopkeeper is holding.
                  const isOut = Number(movement.amount) < 0;

                  return (
                    <tr
                      key={movement.transactionId}
                      className="border-b border-rule last:border-0"
                    >
                      <td className="qs-figure py-1.5 pl-3 text-xs text-ink-muted">
                        {formatDateTime(movement.at, timeZone)}
                        {/* The type, with its column dropped. `font-sans`
                            because the cell around it is the figure face, and a
                            type name is a word rather than a reading. */}
                        <span className="block font-sans text-xs break-words text-ink sm:hidden">
                          {movement.type}
                        </span>
                      </td>
                      <td className="hidden py-1.5 text-xs break-words sm:table-cell">
                        {movement.type}
                      </td>
                      <td className="hidden py-1.5 text-xs text-ink-muted lg:table-cell">{movement.method}</td>
                      <td className="hidden py-1.5 text-xs text-ink-muted lg:table-cell">{movement.tillName}</td>
                      <td
                        className="hidden max-w-56 truncate py-1.5 text-xs text-ink-muted md:table-cell"
                        title={movement.description ?? ''}
                      >
                        {movement.description ?? '—'}
                      </td>
                      <td
                        className={cn(
                          'qs-figure py-1.5 pr-3 text-right text-xs',
                          isOut ? 'text-down' : 'text-up',
                        )}
                      >
                        <span aria-hidden>{isOut ? '▼' : '▲'}</span>{' '}
                        {formatCurrency(movement.amount)}
                      </td>
                      <td className="py-1.5 pr-3 text-right">
                        <ReceiptButton
                          label={`Receipt for transaction #${movement.transactionId}`}
                          onOpen={() =>
                            setViewing({
                              path: `/stores/${storeId}/documents/transaction/${movement.transactionId}`,
                              title: `${movement.type} #${movement.transactionId}`,
                              // Text only. A pay-in receipt is the till roll the
                              // till archived, and `sendArchived` serves that
                              // file whatever format is asked for — so leaving
                              // this to the default offered a PDF button that
                              // fetched the same text back and changed nothing.
                              formats: ['text'],
                            })
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {hasNextPage && (
          <div className="border-t border-rule p-2 text-center">
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="rounded border border-rule px-3 py-1.5 text-xs text-ink-muted hover:border-rule-strong hover:text-ink disabled:opacity-60"
            >
              {isFetchingNextPage ? 'Loading…' : `Load more (${movements.length} shown)`}
            </button>
          </div>
        )}
      </Panel>

      <DocumentViewer request={viewing} onClose={() => setViewing(null)} />
    </>
  );
}
